import React, { useState, useEffect, useCallback, useContext, createContext } from 'react';
import UnifiedAuthPanel from '../UnifiedAuthPanel';
import { useAuth } from '../../contexts/AuthContext';
import { X } from 'lucide-react';

/**
 * Auto-login gate — opens the auth modal only when an action requires auth.
 *
 * Usage pattern in any component:
 *
 *   const { requireAuth } = useRequireAuth();
 *   <button onClick={() => requireAuth(() => doCheckout())}>Buy</button>
 *
 * Behaviour:
 *  - If user is logged in → callback runs immediately, no modal.
 *  - If guest → modal opens with all 3 auth methods (Google · WhatsApp · Email).
 *    The pending callback + intent payload are stashed in sessionStorage so an
 *    OAuth round-trip (Google → callback URL → back) doesn't lose the action.
 *  - After successful auth → callback fires automatically with guest's
 *    original intent restored.
 *
 * Guest browsing is fully preserved — this gate ONLY engages on the action
 * caller's explicit choice.
 */
const PENDING_KEY = 'shopperden_pending_action';
const AutoLoginCtx = createContext({ requireAuth: (_cb) => {}, isOpen: false });

export const useRequireAuth = () => useContext(AutoLoginCtx);

export const AutoLoginGateProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [pendingCb, setPendingCb] = useState(null);

  // Restore a pending action after an OAuth round-trip
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let pending;
    try { pending = sessionStorage.getItem(PENDING_KEY); } catch (_e) { /* ignore */ }
    if (!pending) return undefined;
    try {
      const parsed = JSON.parse(pending);
      sessionStorage.removeItem(PENDING_KEY);
      if (parsed?.redirectTo && window.location.pathname !== parsed.redirectTo) {
        window.location.href = parsed.redirectTo;
      }
    } catch (_e) { /* ignore */ }
    return undefined;
  }, [isAuthenticated]);

  // When auth state flips from false → true while modal is open, fire the queued cb
  useEffect(() => {
    if (isAuthenticated && open) {
      setOpen(false);
      if (typeof pendingCb === 'function') {
        try { pendingCb(user); } catch (_e) { /* ignore */ }
      }
      setPendingCb(null);
    }
  }, [isAuthenticated, open, pendingCb, user]);

  const requireAuth = useCallback((cb, opts = {}) => {
    if (isAuthenticated) {
      if (typeof cb === 'function') cb(user);
      return;
    }
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({
        redirectTo: opts.redirectTo || window.location.pathname + window.location.search,
        intent: opts.intent || null,
        savedAt: new Date().toISOString(),
      }));
    } catch (_e) { /* ignore */ }
    setPendingCb(() => cb);
    setOpen(true);
  }, [isAuthenticated, user]);

  return (
    <AutoLoginCtx.Provider value={{ requireAuth, isOpen: open }}>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
          data-testid="auto-login-gate"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[92vh] overflow-y-auto relative">
            <button
              onClick={() => setOpen(false)}
              data-testid="auto-login-close"
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-800 z-10"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="p-6 pt-12">
              <div className="mb-4 text-center">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4AF37] mb-2">
                  Sign in to continue
                </div>
                <p className="text-xs text-slate-500">
                  Your action is saved — pick the fastest method below.
                </p>
              </div>
              <UnifiedAuthPanel mode="login" />
            </div>
          </div>
        </div>
      )}
    </AutoLoginCtx.Provider>
  );
};

export default AutoLoginGateProvider;
